import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Button from "../../components/Button/Button";
import { RoutesRouter } from "../../models/Routes";
import { useNavigate } from "react-router-dom";
import EmptyTable from "../../components/EmptyTable/EmptyTable";
import { getI18n, useTranslation } from "react-i18next";
import EditPencil from "../../assets/img/edit-pencil.svg";
import SnackChip from "../../components/SnackChip/SnackChip";
import { useEndpoints } from "../../hooks/useEndpoints";
import { CardLibrary } from "../../components/CardLibrary/CardLibrary";
import { KeyStorage, getStorageItem } from "../../utils/localStorage";
import ArchiveSvg from "../../assets/img/archive.svg";
import PublicateSvg from "../../assets/img/publicate.svg";

import { ITemplateButtonOptions } from "../Survey/Template/TemplatePage";
import { Category } from "../../models/SurveyModels";
import Chevron from "../../components/Forms/FormInput/FormSelect/Chevron/Chevron";
import getClasses from "../../utils/getClasses";
import { useComponentVisibles } from "../../utils/functions";
import { PopulatedResource } from "../../models/LibraryModels";
import Skeleton from "react-loading-skeleton";
import { useToastAlert } from "../../components/ToastAlert/ToastAlert";
import { DURATION_TOAST } from "../../utils/costants";

require("./LibraryPage.scss");

const LibraryPage = () => {
  const { showToast } = useToastAlert();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getAllLibraries, patchLibrary, getCategories } = useEndpoints();

  const [libraries, setLibraries] = useState<PopulatedResource[] | undefined>();
  const [filterCategory, setFilterCategory] = useState<
    | ""
    | "ELEARNING"
    | "MICROLEARNING"
    | "CASE_HISTORY"
    | "SUPPORT"
    | "DOCUMENT"
    | "PROJECTS"
    | "HELP"
  >("");
  const isHelpFilter = filterCategory === "HELP";
  const [filterSubCategory, setFilterSUbCategory] = useState<Category>({
    name: t("home.library.filters.allCategories"),
    background: "",
    color: "",
    description: "",
    icon: "",
    id: "",
    coreArea: undefined,
  });
  const [filter, setFilter] = useState<"" | "HR" | "MANAGER" | "EMPLOYEE">("");
  const [inputValue, setInputValue] = React.useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [debouncedInputValue, setDebouncedInputValue] = useState("");
  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };
  const [arrayFilterSubCategory, setArrayFilterSUbCategory] = useState<
    Category[]
  >([]);
  const refs = useRef<(HTMLDivElement | null)[]>([]);
  const handleRefAssignment =
    (index: number) => (ref: HTMLDivElement | null) => {
      refs.current[index] = ref;
    };
  const { isComponentVisible, setComponentVisible } = useComponentVisibles(
    false,
    refs
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedInputValue(inputValue);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [inputValue]);

  useEffect(() => {
    setIsLoading(true);
    getAllLibraries({
      title: debouncedInputValue,
      category:
        filterSubCategory.name === t("home.library.filters.archived")
          ? "archived"
          : filterSubCategory.id,
      type: filterCategory,
      role: filter,
    }).then((resp) => {
      setLibraries(resp);
      setIsLoading(false);
    });
  }, [
    t,
    debouncedInputValue,
    filter,
    filterCategory,
    filterSubCategory,
    getAllLibraries,
  ]);
  useEffect(() => {
    // Le risorse HELP usano categorie dedicate (senza coreArea): al cambio
    // tra Help e altri tipi si ricaricano le categorie e si azzera il filtro
    setFilterSUbCategory({
      name: t("home.library.filters.allCategories"),
      background: "",
      color: "",
      description: "",
      icon: "",
      id: "",
      coreArea: undefined,
    });
    getCategories({
      language: getI18n().language.includes("it") ? "ITALIAN" : "ENGLISH",
      type: isHelpFilter ? "HELP" : undefined,
    }).then((resp) => {
      const filterCategory = [
        {
          name: t("home.library.filters.allCategories"),
          id: "",
          icon: "",
          color: "",
          background: "",
          description: "",
        },
        {
          name: t("home.library.filters.archived"),
          id: "",
          icon: "",
          color: "",
          background: "",
          description: "",
        },
      ];
      setArrayFilterSUbCategory([...filterCategory, ...resp]);
    });
  }, [getCategories, t, isHelpFilter]);

  const generateButtons = useCallback(
    (
      callback: (
        value: NonNullable<any>,
        role: KeyStorage["me"]["role"]
      ) => ITemplateButtonOptions
    ): Array<ITemplateButtonOptions> => {
      if (!libraries) return [] as Array<ITemplateButtonOptions>;
      return libraries?.map((company) => {
        return callback(company, getStorageItem("me")!.role);
      });
    },
    [libraries]
  );

  const changeKeyById = useCallback(
    (id: string): void => {
      patchLibrary(id).then((res) => {
        if (res.status === 200) {
          const lib = libraries?.find((library) => library.id !== id);
          showToast({
            message: `${t(
              `home.library.toast.${lib?.enabled ? "archived" : "published"}`
            )}`,
            type: "success",
            duration: DURATION_TOAST,
          });
          setLibraries((prevLibraries) => {
            const updatedTemplate = prevLibraries?.filter(
              (library) => library.id !== id
            );
            return updatedTemplate;
          });
        }
      });
    },
    [libraries, patchLibrary, showToast, t]
  );

  const buttonsOption = useMemo(
    () =>
      generateButtons((resource: PopulatedResource) => {
        let buttons: ITemplateButtonOptions = [];
        buttons.push(
          {
            onClick: (index: number) => {
              navigate(
                `${RoutesRouter.library}/${
                  libraries && libraries[index].id
                }/edit`
              );
            },
            label: `${t("home.edit")}`,
            colorTheme: "blue",
            icon: <img src={EditPencil} alt="edit" />,
          },
          {
            onClick: (index: number) => {
              changeKeyById(resource.id);
            },
            label: `${t(
              `home.library.actions.${
                resource.enabled ? "archive" : "publicate"
              }`
            )}`,
            colorTheme: "blue",
            icon: resource.enabled ? (
              <img src={ArchiveSvg} alt="edit" />
            ) : (
              <img src={PublicateSvg} alt="edit" />
            ),
          }
        );

        return buttons;
      }),
    [generateButtons, t, navigate, libraries, changeKeyById]
  );

  const filtersLine = () => (
    <>
      <div className="resources-searching container-filters my-3">
        <div className="form-group">
          <div className="col-lg-6 position-relative">
            <input
              id="searchResource"
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              className="form-control filter-questions"
              placeholder={t("home.questions.inputPlaceholder") as string}
            />
            <i className="icon-search"></i>
            {inputValue && (
              <i
                className="icon-close"
                onClick={() => {
                  setInputValue("");
                }}
              ></i>
            )}
          </div>
        </div>
      </div>
      <div className="container-filters row">
        <div className="col-lg-12  row container__top ">
          <div
            ref={handleRefAssignment(1)}
            className="filter-container col-lg-2"
          >
            <div
              className="filter-wrapper col-lg-12"
              onClick={() => {
                setComponentVisible(1);
              }}
            >
              <span className="language-menu filters-label capitalize-first-letter">
                {/*    {t("home.questions.filterActive")}:{" "} */}
                {t(`home.library.filters.${filter === "" ? "all-users" : filter}`)}
              </span>

              <Chevron direction={isComponentVisible[1] ? "top" : "bottom"} />
            </div>
            {isComponentVisible[1] && (
              <div className="switch-filters">
                {(["", "HR", "MANAGER", "EMPLOYEE"] as const).map((item) => (
                  <div
                    className={`filters-item ${getClasses({
                      active: item === filter,
                    })}`}
                    key={item}
                  >
                    <div
                      onClick={() => {
                        setFilter(item);
                        setComponentVisible(1);
                      }}
                      className="menu-item d-flex justify-content-between gap-1"
                    >
                      <span>
                        {t(
                          `home.library.filters.${item === "" ? "all" : item}`
                        )}
                      </span>
                      {item === filter && (
                        <i className="icon icon-checkmark icon-square-checked me-1"></i>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div
            ref={handleRefAssignment(2)}
            className="filter-container col-lg-4"
          >
            <div
              className="filter-wrapper col-lg-12 col-xl-12"
              onClick={() => {
                setComponentVisible(2);
              }}
            >
              <span className="language-menu filters-label capitalize-first-letter">
                {/*                 {t("home.questions.filterActive")}:{" "} */}
                <>{filterSubCategory?.name || t(`selectOptions.ALL`)}</>
              </span>

              <Chevron direction={isComponentVisible[2] ? "top" : "bottom"} />
            </div>
            {isComponentVisible[2] && (
              <div className="switch-filters">
                {arrayFilterSubCategory.map((item, index) => (
                  <div
                    className={`filters-item ${getClasses({
                      active: item.name === filterSubCategory?.name,
                    })}`}
                    key={index}
                  >
                    <div
                      onClick={() => {
                        setFilterSUbCategory(item);
                        setComponentVisible(2);
                      }}
                      className="menu-item d-flex justify-content-between gap-1"
                    >
                      <div>
                        <i
                          style={{
                            color: item.color,
                          }}
                          className={`icon ${item.icon}`}
                        ></i>
                        <span>{item.name}</span>
                      </div>
                      {item.name === filterSubCategory?.name && (
                        <i className="icon icon-checkmark icon-square-checked me-1"></i>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="col-lg-12 filters-wrapper-snackchip with-shadow col-xl-6">
          {(
            [
              "",
              "ELEARNING",
              "MICROLEARNING",
              "CASE_HISTORY",
              "SUPPORT",
              "DOCUMENT",
              "PROJECTS",
              "HELP",
            ] as const
          ).map((item, index) => (
            <SnackChip
              key={index}
              filters
              handleCLick={() => {
                setFilterCategory(item);
              }}
              isActive={item === filterCategory}
              theme="filters"
              label={t(`home.library.filters.${item === "" ? "all" : item}`)}
            />
          ))}
        </div>
      </div>
    </>
  );

  return (
    <div className="container-custom library-container questions-container-page">
      <div className="container__top">
        <div>
          <h1 className="title-page"> {t("home.library.title")}</h1>
          <p>{t("home.library.description")}</p>
        </div>
        <div className="mt-3">
          <Button outline onClick={() => navigate(RoutesRouter.addLibrary)}>
            {t("surveyTemplate.create.createButton")}{" "}
            <span className="plus-char">+</span>
          </Button>
        </div>
      </div>
      {filtersLine()}
      <div>
        {!isLoading && libraries && libraries.length > 0 && (
          <div className=" row wrapper-template-items">
            <CardLibrary
              resources={libraries || []}
              buttonsOption={buttonsOption}
            />
          </div>
        )}
        {!isLoading && libraries?.length === 0 && (
          <EmptyTable
            title={`${t(
              `library.filters.${
                filterCategory === "" ? "all" : filterCategory
              }`
            )}`}
            description={t("library.emptyTableAll")}
          />
        )}
        {isLoading && (
          <Skeleton
            count={8}
            width={"24%"}
            height={310}
            inline
            containerClassName="container-template-skeleton"
          />
        )}
      </div>
    </div>
  );
};

export default LibraryPage;
